import dayjs from 'utils/dayjs';
import {cpServiceGet, reportingPost} from 'api';
import {apiDateFormat, displayDateFormat} from 'utils/date-time';
import {sendSentryException} from 'utils/error-handling';
import {type AxiosRequestConfig} from 'axios';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';
import {
    type AccountingSummaryReportRawResponse,
    type GetAccountingFacilityBreakdownReportRawResponse,
    type GetAccountingReportRequest,
    type GetAccountingReservationReportRawResponse,
} from 'api/accounting/accounting.types';

/**
 * Formats the request data for the accounting api endpoints
 *
 * @param request - The data to format
 *
 * @group DTO Formatters
 */
export const formatAccountingReportRequest = (
    request: GetAccountingReportRequest
) => {
    const formattedRequest = convertKeysToSnakeCase({
        ...request,
        page: request.page,
        fromDate: dayjs(request.fromDate, displayDateFormat).format(
            apiDateFormat
        ),
        toDate: dayjs(request.toDate, displayDateFormat).format(apiDateFormat),
        facilityIds: request.facilityIds.toString(),
    });

    return formattedRequest;
};

/**
 * Gets a collection of accounting data for a given set of facilities
 *
 * @param data - The parameters for the report
 * @param config - The axios config
 *
 * @see '/control-panel-service/v1/accounting/facilities/breakdown/
 */
export const getFacilitiesBreakdownReport = async (
    data: GetAccountingReportRequest,
    config: AxiosRequestConfig = {}
) => {
    try {
        const formattedRequest = formatAccountingReportRequest(data);

        const response = await reportingPost<
            GetAccountingFacilityBreakdownReportRawResponse[]
        >('accounting/facilities/breakdown/', formattedRequest, config);

        const formattedResponse = camelizeKeys(response.data);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Gets the accounting summary data for a given collection of facilities
 *
 * @param data - The parameters for the report
 * @param [config] - The axios config
 *
 * @see '/control-panel-service/v1/accounting/summary/'
 */
export const getAccountingSummaryReport = async (
    data: GetAccountingReportRequest,
    config: AxiosRequestConfig = {}
) => {
    try {
        const formattedRequest = formatAccountingReportRequest(data);

        const response =
            await reportingPost<AccountingSummaryReportRawResponse>(
                'accounting/summary/',
                formattedRequest,
                config
            );
        const formattedResponse = camelizeKeys(response.data);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

/**
 * Gets the accounting summary data for a given collection of facilities
 *
 * @param id - The parameters for the report
 *
 * @see '/api/v1/reservations/{id}/accounting-details/'
 * @see '/api/v1/accounting/refunds/{id}/' (when refundsBookkeeping feature flag is enabled)
 */
export const getReservationDetails = async (id: number) => {
    try {
        const response =
            await cpServiceGet<GetAccountingReservationReportRawResponse>(
                `accounting/refunds/reservation/${id}/?use_bookkeeping=true`
            );

        return response.data;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};
